/**
 * BoSecretariatEnseignantsPage — Liste + CRUD basique des fiches enseignants.
 * Route : /bo/secretariat/enseignants
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, GraduationCap, AlertCircle, X } from 'lucide-react';
import api from '../../api';

const BO_BLUE = '#1d4ed8';
const SPECIALTIES_LABEL = {
  permis_b: 'Permis B', aac: 'AAC', permis_b78: 'B78',
  permis_a1: 'A1', permis_a2: 'A2', passerelle_a: 'Passerelle A',
  formation_125: 'Formation 125', code_seul: 'Code',
};
const COLORS = ['#dc2626', '#059669', '#1d4ed8', '#7c3aed', '#f59e0b', '#0891b2', '#be185d', '#65a30d'];

export default function BoSecretariatEnseignantsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', color: BO_BLUE, specialties: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'Enseignants · Back-office';
    reload();
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.boListEnseignants();
      setItems(r.items || []);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        navigate('/login', { state: { from: '/bo/secretariat/enseignants' } });
        return;
      }
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.boCreateEnseignant({ ...form, name: form.name.trim() });
      setShowAdd(false);
      setForm({ name: '', email: '', phone: '', color: BO_BLUE, specialties: [] });
      reload();
    } catch (e) {
      setError(e.message || 'Création impossible');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Supprimer "${row.name}" ? (leçons associées supprimées aussi)`)) return;
    try {
      await api.boDeleteEnseignant(row.id);
      reload();
    } catch (e) { setError(e.message); }
  };

  const toggleSpec = (key) => {
    setForm((f) => ({
      ...f,
      specialties: f.specialties.includes(key)
        ? f.specialties.filter((s) => s !== key)
        : [...f.specialties, key],
    }));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="bo-enseignants">
      <Link to="/bo" className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/60 hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back-office
      </Link>

      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.22em] mb-1" style={{ color: BO_BLUE }}>
            Secrétariat
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Enseignants ({items.length})
          </h1>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-black shadow-sm hover:opacity-90 transition"
          style={{ background: BO_BLUE }}
          data-testid="btn-add-enseignant"
        >
          <Plus className="w-4 h-4" /> Nouvel enseignant
        </button>
      </div>

      {error && (
        <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-3 mb-4 flex items-start gap-2 text-xs">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" /> {error}
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-foreground/50 py-12 text-center">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-10 text-center">
          <GraduationCap className="w-8 h-8 mx-auto mb-3 text-foreground/40" />
          <p className="text-sm text-foreground/60">Aucun enseignant. Créez-en un ou lancez le seed démo.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {items.map((r) => (
            <div
              key={r.id}
              className="rounded-xl border-2 border-border bg-card p-4 flex items-start gap-3"
              data-testid={`row-enseignant-${r.id}`}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0 font-black"
                style={{ background: r.color || BO_BLUE }}
              >
                {(r.name || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-foreground text-sm truncate">{r.name}</div>
                <div className="text-xs text-foreground/60 truncate">{r.email || '—'}</div>
                {r.specialties && r.specialties.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {r.specialties.slice(0, 3).map((s) => (
                      <span key={s} className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-foreground/70">
                        {SPECIALTIES_LABEL[s] || s}
                      </span>
                    ))}
                    {r.specialties.length > 3 && (
                      <span className="text-[9px] text-foreground/50">+{r.specialties.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleDelete(r)}
                className="p-1.5 rounded hover:bg-red-500/10 text-red-600"
                title="Supprimer"
                data-testid={`btn-delete-ens-${r.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <form
            onSubmit={handleAdd}
            onClick={(e) => e.stopPropagation()}
            className="bg-card rounded-2xl border-2 border-border max-w-md w-full p-5 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            data-testid="modal-add-enseignant"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Nouvel enseignant
              </h2>
              <button type="button" onClick={() => setShowAdd(false)}><X className="w-5 h-5 text-foreground/60" /></button>
            </div>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Nom complet *</span>
              <input
                autoFocus required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                data-testid="input-ens-name"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                data-testid="input-ens-email"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Téléphone</span>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                data-testid="input-ens-phone"
              />
            </label>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Couleur planning</span>
              <div className="mt-1.5 flex gap-1.5 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded-lg transition-transform ${form.color === c ? 'ring-2 ring-offset-2 ring-foreground scale-110' : 'hover:scale-105'}`}
                    style={{ background: c }}
                    aria-label={`Couleur ${c}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Spécialités</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {Object.entries(SPECIALTIES_LABEL).map(([k, v]) => {
                  const on = form.specialties.includes(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => toggleSpec(k)}
                      className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded transition ${on ? 'text-white' : 'bg-muted text-foreground/60 hover:bg-muted/80'}`}
                      style={on ? { background: BO_BLUE } : {}}
                      data-testid={`spec-${k}`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="flex-1 px-3 py-2 rounded-lg border-2 border-border text-sm font-black hover:bg-muted"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-3 py-2 rounded-lg text-white text-sm font-black hover:opacity-90 disabled:opacity-50"
                style={{ background: BO_BLUE }}
                data-testid="btn-submit-ens"
              >
                {saving ? 'Création…' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
